import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../common/Layout/Header';
import UnifiedSidebar from '../common/Layout/Sidebar';
import Footer from '../common/Layout/Footer';
import TitleHeader from '../common/TitleHeader';
import HouseIcon from 'apollo-react-icons/House';
import MedicalCard from 'apollo-react-icons/MedicalCard';
import FileAccountPlan from 'apollo-react-icons/FileAccountPlan';
import Container from 'apollo-react/components/Container';
import CircularProgress from 'apollo-react/components/CircularProgress';
import TranslationReady from '../common/TranslationReady/TranslationReady';
import {
  PageTitleProvider,
  usePageTitle,
} from '../../contexts/PageTitleContext';
import useMenuItems from '../common/Layout/useMenuItems';

import CommonBreadcrumbs, { getPageName } from '../common/CommonBreadcrumbs';

const LayoutContent = () => {
  const location = useLocation();
  const pathnames = location.pathname.split('/').filter((x) => x);
  const [size, setSize] = useState([0, 0]);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const { t, i18n } = useTranslation();
  const { customTitle } = usePageTitle();
  const { menuItems, isLoading: isMenuLoading } = useMenuItems();

  const iconStyles = { fontSize: '36px', color: '#4D4D4D' };
  const pages = [
    { name: 'home', icon: <HouseIcon style={{ ...iconStyles }} /> },
    { name: 'users', icon: <MedicalCard style={{ ...iconStyles }} /> },
    { name: 'endusers', icon: <MedicalCard style={{ ...iconStyles }} /> },
    { name: 'roles', icon: <MedicalCard style={{ ...iconStyles }} /> },
    { name: 'categories', icon: <MedicalCard style={{ ...iconStyles }} /> },
    { name: 'logs', icon: <FileAccountPlan style={{ ...iconStyles }} /> },
  ];

  useEffect(() => {
    const [width] = size;
    if (width < 850 && width !== 0) setIsSidebarCollapsed(true);
  }, [size]);

  useEffect(() => {
    const updateSize = () => {
      setSize([window.innerWidth, window.innerHeight]);
    };
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  useEffect(() => {
    const savedSidebarState =
      localStorage.getItem('isSidebarCollapsed') === 'true';
    setIsSidebarCollapsed(savedSidebarState);
  }, []);

  const handleSidebarToggle = (collapsed) => {
    setIsSidebarCollapsed(collapsed);
    if (collapsed) {
      localStorage.setItem('isSidebarCollapsed', true);
    } else {
      localStorage.removeItem('isSidebarCollapsed');
    }
  };

  const pageName = getPageName('end-user', location);
  const translatedPageName = (() => {
    const lowerPageName = pageName.toLowerCase();
    const underscorePageName = lowerPageName.replace(/\s+/g, '_');
    const dashedPageName = lowerPageName.replace(/\s+/g, '-');

    return (
      t(underscorePageName) || t(dashedPageName) || t(lowerPageName) || pageName
    );
  })();

  if (isMenuLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  return (
    <TranslationReady>
      <div>
        <Container
          className="main-container"
          style={{
            marginLeft: isSidebarCollapsed ? '64px' : '320px',
            width: isSidebarCollapsed ? '100%' : '75%',
          }}
        >
          <Header />

          <UnifiedSidebar
            menuItems={menuItems}
            isSidebarCollapsed={isSidebarCollapsed}
            setIsSidebarCollapsed={handleSidebarToggle}
          />

          <main className="main-content">
            <CommonBreadcrumbs
              source="end-user"
              pathnames={pathnames}
              location={location}
            />

            <TitleHeader
              title={customTitle || translatedPageName}
              icon={pages.find((page) => page.name === pageName)?.icon}
            />

            <Outlet key={i18n.language} />
          </main>

          <Footer />
        </Container>
      </div>
    </TranslationReady>
  );
};

// Wrap with PageTitleProvider to allow child components to override the page title
const Layout = () => (
  <PageTitleProvider>
    <LayoutContent />
  </PageTitleProvider>
);

export default Layout;
